import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Camera as CameraIcon,
  Mic as MicIcon,
  FiberManualRecord as RecordIcon,
} from "@mui/icons-material";
import { useNavigate, useParams , useLocation} from "react-router-dom";
import { AxiosService } from "../../../services/axios/axiosService";
import toast from "react-hot-toast";
import permissionAlertImage from "../../../assets/images/permission-alert.png";

const isPermissionDeniedError = (err) =>
  err?.name === "NotAllowedError" ||
  err?.name === "PermissionDeniedError";

const CandidateDeviceCheck = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const { interviewId } = useLocation().state || {};

  const [checks, setChecks] = useState({
    camera: { status: "pending", message: "Checking...", error: null },
    microphone: { status: "pending", message: "Checking...", error: null },
  });
  const { token } = useParams();

  const [isError, setIsError] = useState('');
  const [isTestingComplete, setIsTestingComplete] = useState(false);
  const [allChecksPassed, setAllChecksPassed] = useState(false);
  const [isRetesting, setIsRetesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [deniedDevices, setDeniedDevices] = useState([]); // ["camera","microphone"]
  const hasRun = useRef(false);
  const openSystemPrivacySettings = (type) => {
    // Best-effort: works on Windows when browser allows external protocol handling.
    // Falls back to our "steps" dialog if blocked.
    const isWindows = /Windows/i.test(navigator.userAgent);
    if (!isWindows) return false;

    const uri =
      type === "microphone"
        ? "ms-settings:privacy-microphone"
        : "ms-settings:privacy-webcam"; // default to camera settings when no explicit type

    try {
      window.location.assign(uri);
      return true;
    } catch {
      return false;
    }
  };

  // Check camera
  const checkCamera = useCallback(async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        camera: {
          status: "testing",
          message: "Testing camera...",
          error: null,
        },
      }));

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // Verify video track is active
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack && videoTrack.readyState === "live") {
        setChecks((prev) => ({
          ...prev,
          camera: {
            status: "success",
            message: "Working properly",
            error: null,
          },
        }));
        return true;
      } else {
        throw new Error("Video track not active");
      }
    } catch (error) {
      let errorMessage = "Camera access denied or unavailable";
      if (error.name === "NotAllowedError") {
        errorMessage = "Camera permission denied. Please allow camera access.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No camera device found";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Camera is already in use by another application";
      }

      setChecks((prev) => ({
        ...prev,
        camera: { status: "error", message: errorMessage, error: error.name },
      }));
      return { ok: false, denied: isPermissionDeniedError(error) };
    }
  }, []);

  // Check microphone
  const checkMicrophone = useCallback(async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        microphone: {
          status: "testing",
          message: "Testing microphone...",
          error: null,
        },
      }));

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      // Create audio context to analyze microphone input
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;

      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      analyser.fftSize = 256;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Test for audio input
      return new Promise((resolve) => {
        let hasSound = false;
        let attempts = 0;
        const maxAttempts = 30; // 3 seconds

        const checkAudioLevel = () => {
          attempts++;
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;

          if (average > 10) {
            hasSound = true;
          }

          if (attempts >= maxAttempts || hasSound) {
            stream.getTracks().forEach((track) => track.stop());

            if (hasSound || average > 0) {
              setChecks((prev) => ({
                ...prev,
                microphone: {
                  status: "success",
                  message: "Working properly",
                  error: null,
                },
              }));
              resolve({ ok: true, denied: false });
            } else {
              setChecks((prev) => ({
                ...prev,
                microphone: {
                  status: "success",
                  message: "Connected (speak to test)",
                  error: null,
                },
              }));
              resolve({ ok: true, denied: false });
            }
          } else {
            setTimeout(checkAudioLevel, 100);
          }
        };

        checkAudioLevel();
      });
    } catch (error) {
      let errorMessage = "Microphone access denied or unavailable";
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Microphone permission denied. Please allow microphone access.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No microphone device found";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Microphone is already in use by another application";
      }

      setChecks((prev) => ({
        ...prev,
        microphone: {
          status: "error",
          message: errorMessage,
          error: error.name,
        },
      }));
      return { ok: false, denied: isPermissionDeniedError(error) };
    }
  }, []);

  // Run all checks
  const runAllChecks = useCallback(async () => {
    setIsRetesting(true);
    setIsTestingComplete(false);
    setAllChecksPassed(false);
    setPermissionModalOpen(false);
    setDeniedDevices([]);

    // Run checks sequentially to avoid device conflicts
    const cameraResult = await checkCamera();
    const microphoneResult = await checkMicrophone();

    const cameraOk = cameraResult === true || cameraResult?.ok === true;
    const microphoneOk =
      microphoneResult === true || microphoneResult?.ok === true;

    const allPassed = cameraOk && microphoneOk;

    setAllChecksPassed(allPassed);
    setIsTestingComplete(true);
    setIsRetesting(false);

    const denied = [];
    if (!cameraOk && cameraResult?.denied) {
      denied.push("camera");
    }
    if (!microphoneOk && microphoneResult?.denied) {
      denied.push("microphone");
    }
    // If blocked by browser permission, show an explicit step before proceeding.
    if (denied.length > 0) {
      setDeniedDevices(denied);
      setPermissionModalOpen(true);
    }
  }, [checkCamera, checkMicrophone]);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    runAllChecks();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (!allChecksPassed) return;
  
    const interval = setInterval(() => {
      const stream = streamRef.current;
      const isLive = stream && stream.getTracks().some((t) => t.readyState === "live");
  
      if (!isLive) {
        // Stream died — user reset permissions without reloading
        setAllChecksPassed(false);
        setIsTestingComplete(true);
        setChecks((prev) => ({
          ...prev,
          camera: {
            status: "error",
            message: "Camera access was revoked. Please allow access and test again.",
            error: "NotAllowedError",
          },
        }));
        setDeniedDevices(["camera", "microphone"]);
        setPermissionModalOpen(true); // show modal immediately
      }
    }, 2000);
  
    return () => clearInterval(interval);
  }, [allChecksPassed]);

  const handleRequestAccess = () => {
    // Stop existing streams
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    runAllChecks();
  };

  const handleStartInterview = async () => {
    // Re-validate stream is actually live at click time
    const stream = streamRef.current;
    const isLive = stream && stream.getTracks().some((t) => t.readyState === "live");
  
    if (!allChecksPassed || !isLive) {
      setAllChecksPassed(false);
      setIsTestingComplete(true);
      setChecks((prev) => ({
        ...prev,
        camera: {
          status: "error",
          message: "Camera access lost. Please allow access and test again.",
          error: "NotAllowedError",
        },
      }));
      setDeniedDevices(["camera", "microphone"]);
      setPermissionModalOpen(true);
      return;
    }
  
    setLoading(true);
    try {
      await AxiosService.post(`/api/Interview/${interviewId}/complete-deviceCheck`);
      navigate(`/interview/${token}`, { state: { interviewId: interviewId } });
    } catch (err) {
      setIsError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return { bg: "#e8f5e9", border: "#c8e6c9", text: "#2e7d32" };
      case "warning":
        return { bg: "#fff3e0", border: "#ffe0b2", text: "#ef6c00" };
      case "error":
        return { bg: "#ffebee", border: "#ffcdd2", text: "#c62828" };
      default:
        return { bg: "#f5f5f5", border: "#e0e0e0", text: "#757575" };
    }
  };

  const renderDeviceCard = (type, icon, label) => {
    const check = checks[type];
    const colors = getStatusColor(check.status);
    const Icon = icon;

    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          bgcolor: colors.bg,
          borderRadius: 3,
          border: `1px solid ${colors.border}`,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Icon sx={{ color: colors.text, fontSize: 30 }} />
          {check.status === "testing" && <CircularProgress size={20} />}
          {check.status === "success" && (
            <CheckCircleIcon sx={{ color: colors.text, fontSize: 24 }} />
          )}
          {check.status === "error" && (
            <ErrorIcon sx={{ color: colors.text, fontSize: 24 }} />
          )}
        </Box>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.text }}>
          {check.message}
        </Typography>
      </Paper>
    );
  };

  return (
    <Card
      sx={{
        maxWidth: "md",
        mx: "auto",
        mt: 4,
        mb: 3,
        border: "1px solid #E0E0E0",
        boxShadow: "none",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Dialog
          open={permissionModalOpen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              setPermissionModalOpen(false);
            }
          }}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(2px)",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }} color="warning">
            Action Required!
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Camera and microphone access are required to continue the interview.
            Please click the <b>lock or camera icon</b> in your browser's address bar, allow access, then reload the page to continue.
            </Typography>
            
            <Box
              component="img"
              src={permissionAlertImage}
              alt="Browser address bar showing camera permission icon"
              sx={{
                mt: 2,
                width: "100%",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            />
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            <strong>Note:</strong> The icon in the address bar may vary depending on your
            browser (e.g. 🔒 lock, 🎥 camera, or ℹ️ info icon).
          </Typography>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 2.5 }} >
            <Button
              variant="contained"
              onClick={() => {
                // Simple flow: just dismiss the warning.
                // User can click "Test Again" after changing permissions.
                setPermissionModalOpen(false);
              }}
              sx={{ textTransform: "none" }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        {/* <Dialog
          open={permissionHelpOpen}
          onClose={() => setPermissionHelpOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Enable camera & microphone permissions
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                1) Click the <b>lock</b> / <b>camera</b> icon near the address bar.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                2) Set <b>Camera</b> and <b>Microphone</b> to <b>Allow</b>.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                3) Come back here and reload the page.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Note: If your browser is set to “Block”, it may not show the popup again until you change the site permission.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              variant="outlined"
              onClick={() => {
                // Open Windows camera privacy settings (best effort).
                openSystemPrivacySettings("camera");
              }}
              sx={{ textTransform: "none" }}
            >
              Open privacy settings
            </Button>
            <Button
              variant="outlined"
              onClick={() => setPermissionHelpOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog> */}

        <Box sx={{ px: 4, pt: 2.5 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Device Check
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ensure your camera, microphone, and internet connection are working
            properly
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "#E0E0E0" }} />

        <Box sx={{ px: 4, py: 2.5 }}>
          {isError && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {isError}
            </Alert>
          )}
          {!allChecksPassed && isTestingComplete && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              Device checks failed.If you previously blocked access, allow them in your browser (lock icon in the address bar) before testing again.
            </Alert>
          )}

          <Box
            sx={{
              bgcolor: "#1a1f3a",
              borderRadius: 3,
              height: 350,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {checks.camera.status === "success" && (
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  zIndex: 1,
                }}
              >
                <RecordIcon sx={{ color: "#ff4444", fontSize: 12 }} />
                <Typography
                  variant="caption"
                  sx={{ color: "white", fontSize: 11 }}
                >
                  Camera Active
                </Typography>
              </Box>
            )}

            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: checks.camera.status === "success" ? "block" : "none",
              }}
            />

            {checks.camera.status !== "success" && (
              <CameraIcon sx={{ fontSize: 60, color: "#6b7099" }} />
            )}
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
            {renderDeviceCard("camera", CameraIcon, "Camera")}
            {renderDeviceCard("microphone", MicIcon, "Microphone")}
          </Stack>       

          <Box
            sx={{
              mb: 3,
              p: 3,
              bgcolor: "#F9F9F9",
              borderRadius: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ mb: 2, color: "#333" }}
            >
              Best Practices for Interview
            </Typography>
            <Stack spacing={1.5}>
  {[
    "Position yourself in a well-lit area facing the camera.",
    "Ensure your face is clearly visible throughout the interview.",
    "Minimize background noise and distractions.",
    "Allow camera and microphone access.",
    "Stay focused on the interview tab (switching tabs may be flagged).",
    "Speak clearly and at a normal pace.",
  ].map((tip) => (
    <Box key={tip} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "#9e9e9e",
          mt: "5px",
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.5 }}>
        {tip}
      </Typography>
    </Box>
  ))}
</Stack>
          </Box>

          <Divider sx={{ borderColor: "#E0E0E0" }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleRequestAccess}
              size="large"
              disabled={isRetesting}
              color="secondary"
            >
              {isRetesting ? "Testing..." : "Test Again"}
            </Button>

            <Button
              type="submit"
              variant="contained"
              size="large"
              onClick={handleStartInterview}
              disabled={!allChecksPassed || isRetesting || loading}
              color="primary"
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
            {allChecksPassed
              ? "Start Interview"
              : isRetesting
              ? "Checking devices..."
              : "Complete Device Checks to Continue"}
              </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CandidateDeviceCheck;
